import { useState, useMemo/*, useEffect, useRef*/ } from 'react';
import { useParams } from 'react-router-dom';
import { useDevice, useSensorData, useFieldPending, useMTCommand } from '@/hooks';
import { NAMTMessageType } from '@/types/enums';
import { Button } from '@/components/common/Button/Button';
import { StatusBadge } from '@/components/common/StatusBadge/StatusBadge';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import { ToggleSwitch, PendingBadge } from '@/components/common/ConfigControls';
import { findPendingMT } from '@/lib/sensor-commands';
import { formatVoltage, formatDateTime } from '@/lib/formatters';
import { resolveOnlineState, onlineStateVariant } from '@/lib/enum-resolvers';
import { isIntegratedSensor } from '@/lib/sensor-map';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';

/** Compute the 64-bit address long string used by the server descriptor commands. */
function toLongAddressStr(msb: number, lsb: number): string {
  return String(BigInt(msb) * BigInt(0x100000000) + BigInt(lsb));
}

function getVoltagePercent(voltage: number, min: number, max: number): number {
  if (max <= min) return 0;
  return Math.max(0, Math.min(100, ((voltage - min) / (max - min)) * 100));
}

function getVoltageBarColor(voltage: number, alertLevel: number, criticalLevel: number): string {
  if (voltage <= criticalLevel) return 'bg-red-500';
  if (voltage <= alertLevel) return 'bg-yellow-500';
  return 'bg-green-500';
}

function getVoltageZoneColor(voltage: number, alertLevel: number, criticalLevel: number): string {
  if (voltage <= criticalLevel) return 'text-red-500';
  if (voltage <= alertLevel) return 'text-yellow-500';
  return 'text-green-500';
}

export function DCBanksPanel() {
  const { deviceUUID } = useParams<{ deviceUUID: string }>();
  const { selectedDeviceInfo, deviceInfoLoading } = useDevice();
  const { sensorData } = useSensorData();

  const [refreshing, setRefreshing] = useState(false);
  const [selectedTrendBank, setSelectedTrendBank] = useState<number | null>(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [editingBankNum, setEditingBankNum] = useState<number | null>(null);
  const [editingDescriptor, setEditingDescriptor] = useState('');
  const [descriptorSaving, setDescriptorSaving] = useState(false);
  // Immediate optimistic value + pending flag per bank field (Monitoring
  // toggle, descriptor) — same hook used in Configuration, so a toggle/save
  // doesn't visibly revert to the stale server value until the next poll.
  const fieldPending = useFieldPending();
  const { sendCommand, mtObjList, feedback, setFeedback } = useMTCommand(deviceUUID ?? '');

  // const requestedBanks = useRef<Set<number>>(new Set());

  const dcBanks = sensorData?.dcSensorList ?? [];
  const dcTrendList = sensorData?.dcTrendList ?? [];

  // TODO: Re-enable auto-request per-bank settings when settingsInitialized is false.
  // Guard: only send when the device is actually connected (mirrors mobile app dcTrendReqCallback).
  // useEffect(() => {
  //   if (!deviceUUID || dcBanks.length === 0) return;
  //   const props = selectedDeviceInfo?.deviceProperties;
  //   if (!props?.isDirectConnected && !props?.isIoTCoreConnected) return;
  //   dcBanks.forEach((bank) => {
  //     if (!bank.settingsInitialized && !requestedBanks.current.has(bank.dcBankNum)) {
  //       requestedBanks.current.add(bank.dcBankNum);
  //       commandService.sendOnDemandCommand({
  //         deviceUUID,
  //         mtMessageType: NAMTMessageType.GET_SENSOR_DATA_REQUEST,
  //         dataStrList: [String(bank.dcBankNum)],
  //       });
  //     }
  //   });
  // }, [deviceUUID, dcBanks, selectedDeviceInfo]);

  const handleSaveDescriptor = async (bank: { sourceAddressMSB: number; sourceAddressLSB: number; dcBankNum: number }) => {
    if (!deviceUUID) return;
    setDescriptorSaving(true);
    const newDescriptor = editingDescriptor;
    try {
      await fieldPending.run(`descriptor-${bank.dcBankNum}`, newDescriptor, () =>
        sendCommand(NAMTMessageType.MT_MESSAGE_SET_DC_DESCRIPTORS, [
          toLongAddressStr(bank.sourceAddressMSB, bank.sourceAddressLSB),
          String(bank.dcBankNum),
          newDescriptor,
        ]),
      );
    } finally {
      setDescriptorSaving(false);
      setEditingBankNum(null);
    }
  };

  // Keep the button spinning until the device actually receives the command
  // (or the pending window times out), not just until the enqueue POST
  // returns — the REST call resolves as soon as it's queued, well before the
  // device checks in.
  const refreshPending = refreshing || !!findPendingMT(mtObjList, NAMTMessageType.GET_SENSOR_DATA_REQUEST);

  const handleRefresh = async () => {
    if (!deviceUUID) return;
    setRefreshing(true);
    try {
      await sendCommand(NAMTMessageType.GET_SENSOR_DATA_REQUEST);
    } finally {
      setRefreshing(false);
    }
  };

  const trendPending =
    trendLoading ||
    (selectedTrendBank != null &&
      !!findPendingMT(mtObjList, NAMTMessageType.GET_DC_TREND_DATA, (d) => d[0] === String(selectedTrendBank)));

  const handleLoadTrend = async (bankNum: number) => {
    if (!deviceUUID) return;
    setSelectedTrendBank(bankNum);
    setTrendLoading(true);
    try {
      await sendCommand(NAMTMessageType.GET_DC_TREND_DATA, [String(bankNum)]);
    } finally {
      setTrendLoading(false);
    }
  };

  const handleToggleEnabled = (bankNum: number, currentlyEnabled: boolean) => {
    if (!deviceUUID) return;
    setFeedback(null);
    return fieldPending.run(`enabled-${bankNum}`, !currentlyEnabled, () =>
      sendCommand(
        currentlyEnabled ? NAMTMessageType.DC_BANK_SET_DISABLED : NAMTMessageType.DC_BANK_SET_ENABLED,
        [String(bankNum)],
      ),
    );
  };

  // Build trend chart data from the current dcTrendList
  const trendChartData = useMemo(() => {
    if (dcTrendList.length === 0) return [];
    const trend = dcTrendList[0];
    const buffer = trend.dcVoltageBuff ?? [];
    const sampleRate = trend.sampleRateMinutes ?? 15;
    const refTime = trend.updatedDT ? new Date(trend.updatedDT).getTime() : Date.now();
    return buffer.map((voltage: number, idx: number) => {
      const minutesOffset = (buffer.length - 1 - idx) * sampleRate;
      const dt = new Date(refTime - minutesOffset * 60000);
      const label = `${dt.getHours().toString().padStart(2, '0')}:${dt.getMinutes().toString().padStart(2, '0')}`;
      return { label, voltage };
    });
  }, [dcTrendList]);

  if (deviceInfoLoading && !selectedDeviceInfo) {
    return <PageSpinner />;
  }

  if (!selectedDeviceInfo) {
    return (
      <EmptyState
        title="No Device Selected"
        description="Select a device to view DC battery banks."
      />
    );
  }

  if (dcBanks.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">DC Banks</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Monitor battery bank voltages and status
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={handleRefresh} loading={refreshPending}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
            </svg>
            Refresh
          </Button>
        </div>
        <EmptyState
          title="No DC Banks"
          description="No battery banks are configured on this device."
          icon={
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 10.5h.375c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125H21M3.75 18h15A2.25 2.25 0 0021 15.75v-6a2.25 2.25 0 00-2.25-2.25h-15A2.25 2.25 0 001.5 9.75v6A2.25 2.25 0 003.75 18z" />
            </svg>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">DC Banks</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Monitor battery bank voltages and status
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleRefresh} loading={refreshPending}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
          </svg>
          Refresh
        </Button>
      </div>

      {feedback && (
        <div
          className={`rounded-lg border p-3 text-sm ${
            feedback.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400'
              : 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400'
          }`}
        >
          <div className="flex items-center justify-between">
            <span>{feedback.message}</span>
            <button type="button" onClick={() => setFeedback(null)} className="ml-3 text-xs font-medium underline">
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {dcBanks.map((bank, idx) => {
          const percent = getVoltagePercent(bank.dcVoltage, bank.minVoltage, bank.maxVoltage);
          const barColor = getVoltageBarColor(bank.dcVoltage, bank.alertLevel, bank.criticalLevel);
          const voltageColor = getVoltageZoneColor(bank.dcVoltage, bank.alertLevel, bank.criticalLevel);
          // Pending descriptor commands carry the new value in their own
          // payload (dataStrList[2]) — fall through to it once the local
          // optimistic value clears (right after the enqueue POST resolves)
          // so the field keeps showing the new value for the whole time the
          // command is actually pending on the device, not just during the
          // POST.
          const pendingDescriptorMT = findPendingMT(
            mtObjList,
            NAMTMessageType.MT_MESSAGE_SET_DC_DESCRIPTORS,
            (d) => d[1] === String(bank.dcBankNum),
          );
          const descriptor =
            fieldPending.getOptimisticValue<string>(`descriptor-${bank.dcBankNum}`) ??
            pendingDescriptorMT?.dataStrList?.[2] ??
            bank.descriptor;
          const isEnabled =
            fieldPending.getOptimisticValue<boolean>(`enabled-${bank.dcBankNum}`) ?? bank.isEnabled;
          const enabledPending =
            fieldPending.isPending(`enabled-${bank.dcBankNum}`) ||
            !!findPendingMT(mtObjList, NAMTMessageType.DC_BANK_SET_ENABLED, (d) => d[0] === String(bank.dcBankNum)) ||
            !!findPendingMT(mtObjList, NAMTMessageType.DC_BANK_SET_DISABLED, (d) => d[0] === String(bank.dcBankNum));
          // Same alarm-driven card tint as Intrusion/Bilge-Float — mirrors the
          // dashboard tile's own dcCritical > 0 ? danger : dcAlerting > 0 ?
          // warning severity ordering (ApplicationsPage.tsx), which this card
          // previously didn't carry down to the individual-bank level.
          const cardTint = bank.criticalAlertPresent
            ? 'border-red-400 bg-red-50 dark:border-red-600 dark:bg-red-900/10'
            : bank.alertPresent
              ? 'border-yellow-400 bg-yellow-50 dark:border-yellow-600 dark:bg-yellow-900/10'
              : 'border-slate-200 bg-white dark:border-navy-700 dark:bg-navy-900';

          return (
            <div
              key={`${bank.dcBankNum}-${idx}`}
              className={`rounded-xl border p-5 ${cardTint}`}
            >
              {/* Header */}
              <div className="mb-3 flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 dark:text-white">
                    {descriptor || `Bank ${bank.dcBankNum}`}
                  </h3>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {/* Mobile's "{sensorName}, Bank {N}" (updateDCCategory,
                        DeviceCategoryMetrics.cs) — bankInstance is per-physical-sensor
                        and 0-based, so +1 gives the bank's position on its own board,
                        unlike dcBankNum's device-wide enumeration. */}
                    {bank.sensorName}, Bank {bank.bankInstance + 1} &middot; {bank.is24V ? '24V' : '12V'}
                  </p>
                </div>
                <div className="ml-2 flex flex-col items-end gap-1">
                  {isIntegratedSensor(bank) ? (
                    <StatusBadge label="Built-in" variant="info" dot />
                  ) : (
                    <StatusBadge
                      label={resolveOnlineState(bank.onlineState)}
                      variant={onlineStateVariant(bank.onlineState)}
                      dot
                    />
                  )}
                  {bank.criticalAlertPresent && (
                    <StatusBadge label="CRITICAL" variant="danger" dot />
                  )}
                  {bank.alertPresent && !bank.criticalAlertPresent && (
                    <StatusBadge label="Alert" variant="warning" dot />
                  )}
                </div>
              </div>

              {/* Descriptor */}
              <div className="mb-3 flex items-center text-xs">
                {editingBankNum === bank.dcBankNum ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      autoFocus
                      maxLength={15}
                      value={editingDescriptor}
                      onChange={(e) => setEditingDescriptor(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveDescriptor(bank);
                        if (e.key === 'Escape') setEditingBankNum(null);
                      }}
                      className="w-32 rounded border border-teal-400 bg-transparent px-1.5 py-0.5 text-xs text-slate-800 dark:text-slate-100 focus:outline-none"
                    />
                    <button
                      type="button"
                      disabled={descriptorSaving}
                      onClick={() => handleSaveDescriptor(bank)}
                      className="text-teal-500 hover:text-teal-400 disabled:opacity-50"
                    >
                      {descriptorSaving ? '…' : '✓'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingBankNum(null)}
                      className="text-slate-400 hover:text-slate-300"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setEditingBankNum(bank.dcBankNum); setEditingDescriptor(descriptor ?? ''); }}
                    className="flex items-center gap-1 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                  >
                    <span className="font-medium text-slate-700 dark:text-slate-300">
                      {descriptor || <em className="text-slate-400">No description</em>}
                    </span>
                    <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
                    </svg>
                  </button>
                )}
                {(fieldPending.isPending(`descriptor-${bank.dcBankNum}`) || !!pendingDescriptorMT) && (
                  <PendingBadge />
                )}
              </div>

              {/* Voltage display */}
              <div className="mb-4 text-center">
                <span className={`text-4xl font-bold ${voltageColor}`}>
                  {(bank.dataValid || bank.dcVoltage > 0) ? formatVoltage(bank.dcVoltage) : '--.-V'}
                </span>
              </div>

              {/* Voltage bar */}
              <div className="mb-2">
                <div className="h-3 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-navy-700">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${bank.settingsInitialized ? barColor : 'bg-slate-400'}`}
                    style={{ width: bank.settingsInitialized ? `${percent}%` : '0%' }}
                  />
                </div>
                <div className="mt-1 flex justify-between text-xs text-slate-400 dark:text-slate-500">
                  <span>{bank.settingsInitialized ? formatVoltage(bank.minVoltage) : '—'}</span>
                  <span>{bank.settingsInitialized ? formatVoltage(bank.maxVoltage) : '—'}</span>
                </div>
              </div>

              {/* Monitoring toggle */}
              <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 dark:border-navy-700">
                <span className="flex items-center text-xs text-slate-500 dark:text-slate-400">
                  Monitoring
                  {enabledPending && <PendingBadge />}
                </span>
                <ToggleSwitch
                  enabled={isEnabled}
                  loading={false}
                  onToggle={() => handleToggleEnabled(bank.dcBankNum, isEnabled)}
                />
              </div>

              {/* Updated time */}
              {bank.updatedDT && (
                <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">
                  Updated: {formatDateTime(bank.updatedDT)}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* DC Trend History */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 dark:border-navy-700 dark:bg-navy-900">
        <h2 className="mb-1 text-lg font-semibold text-slate-900 dark:text-white">DC Trend History</h2>
        <p className="mb-4 text-xs text-slate-500 dark:text-slate-400">
          Select a bank to load its voltage trend. Only one bank can be trended at a time.
        </p>

        {/* Bank selector — one trend active at a time */}
        <div className="mb-4 flex flex-wrap gap-2">
          {dcBanks.map((bank) => (
            <Button
              key={bank.dcBankNum}
              variant={selectedTrendBank === bank.dcBankNum ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => handleLoadTrend(bank.dcBankNum)}
              loading={trendPending && selectedTrendBank === bank.dcBankNum}
            >
              {bank.descriptor || `Bank ${bank.dcBankNum}`}
            </Button>
          ))}
        </div>

        {/* Trend chart or empty state */}
        {dcTrendList.length === 0 || trendChartData.length === 0 ? (
          <div className="flex h-[250px] items-center justify-center rounded-lg bg-slate-50 dark:bg-navy-800">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No trend data available. Select a bank and tap Refresh to load trend data.
            </p>
          </div>
        ) : (() => {
          const activeTrend = dcTrendList[0];
          const voltages = trendChartData.map((d) => d.voltage as number).filter((v) => v > 0);
          const allValues = [...voltages, activeTrend.maxVoltage, activeTrend.minVoltage].filter((v) => v > 0);
          const yMin = allValues.length > 0 ? Math.max(0, Math.min(...allValues) - 0.5) : 0;
          const yMax = allValues.length > 0 ? Math.max(...allValues) + 0.5 : 20;
          return (
            <div>
              {activeTrend.bankNum != null && (
                <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
                  Showing trend for Bank {activeTrend.bankNum}
                </p>
              )}
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={trendChartData} margin={{ top: 5, right: 80, bottom: 5, left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <YAxis domain={[yMin, yMax]} tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={(v: number) => `${v.toFixed(1)}V`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                    labelStyle={{ color: '#94a3b8' }}
                    formatter={(value: unknown) => [formatVoltage(value as number), 'Voltage']}
                  />
                  {activeTrend.maxVoltage > 0 && (
                    <ReferenceLine
                      y={activeTrend.maxVoltage}
                      stroke="#22c55e"
                      strokeDasharray="5 5"
                      strokeWidth={1}
                      label={{ value: `Normal ${formatVoltage(activeTrend.maxVoltage)}`, position: 'right', fontSize: 10, fill: '#22c55e' }}
                    />
                  )}
                  {activeTrend.minVoltage > 0 && (
                    <ReferenceLine
                      y={activeTrend.minVoltage}
                      stroke="#ef4444"
                      strokeDasharray="5 5"
                      strokeWidth={1}
                      label={{ value: `Alert ${formatVoltage(activeTrend.minVoltage)}`, position: 'right', fontSize: 10, fill: '#ef4444' }}
                    />
                  )}
                  <Line
                    type="monotone"
                    dataKey="voltage"
                    stroke="#06B6D4"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
              {/* Date label — close to x axis */}
              {activeTrend.updatedDT && (
                <p className="mt-1 text-center text-[10px] text-slate-400 dark:text-slate-500">
                  Captured{' '}
                  {new Date(activeTrend.updatedDT).toLocaleDateString(undefined, {
                    month: 'short', day: 'numeric', year: 'numeric',
                  })}{' '}at{' '}
                  {new Date(activeTrend.updatedDT).toLocaleTimeString(undefined, {
                    hour: '2-digit', minute: '2-digit',
                  })}
                </p>
              )}

              <div className="mt-3 flex flex-wrap justify-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-1.5"><span className="h-0.5 w-4 bg-cyan-500" /> Voltage</span>
                {activeTrend.maxVoltage > 0 && (
                  <span className="flex items-center gap-1.5"><span className="h-0.5 w-4 border-t-2 border-dashed border-green-500" /> Normal</span>
                )}
                {activeTrend.minVoltage > 0 && (
                  <span className="flex items-center gap-1.5"><span className="h-0.5 w-4 border-t-2 border-dashed border-red-500" /> Alert</span>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
